// web/src/screens/admin/shared_edit_confirm.ts
//
// ADMIN-07b — shared-edit confirm flow.
//
// When the inline LV save path mutates a Text/TextSet that is also referenced
// by another parent entity, the server returns 422 with body
// {code:"shared_edit_requires_confirm", references:[...], kind, id, message}.
// saveWithSharedEditConfirm wraps adminPatch:
//   1. Try the PATCH normally.
//   2. If the 422 matches the shared_edit_requires_confirm shape, show a
//      modal listing the foreign references + a deep-link to the
//      /admin/<texts|textsets>/<id> page where the "Used by" panel shows
//      everything.
//   3. On user confirm → retry the same PATCH against `${path}?confirm_shared=1`.
//   4. On cancel → throw the original error so the caller renders a "Save
//      cancelled — no changes applied" hint.
//
// The modal handles ONE 422 at a time (text OR textset). If multiple slots
// on the same PATCH triggered shared edits, the server only reports the first
// — confirming applies confirm to ALL slots in the same retry. That's a
// reasonable v1 default; future work can broaden the payload to a list.

import { adminPatch, AdminError } from "../../api/admin.js";
import { openModal } from "./modal.js";
import type { AdminSharedEditError } from "../../types/api.js";

const SHARED_EDIT_CODE = "shared_edit_requires_confirm";

// saveWithSharedEditConfirm: PATCH `path` with `body`. On a 422 carrying the
// shared_edit_requires_confirm code, prompt the user and retry with
// ?confirm_shared=1. Any other error propagates unchanged.
export async function saveWithSharedEditConfirm<TIn, TOut>(
  path: string,
  body: TIn,
): Promise<TOut> {
  try {
    return await adminPatch<TIn, TOut>(path, body);
  } catch (err) {
    if (!(err instanceof AdminError) || err.status !== 422) throw err;
    const env = err.jsonBody<AdminSharedEditError>();
    if (!env || env.code !== SHARED_EDIT_CODE) throw err;
    const ok = await promptSharedEditConfirm(env);
    if (!ok) throw err;
    const sep = path.includes("?") ? "&" : "?";
    return await adminPatch<TIn, TOut>(`${path}${sep}confirm_shared=1`, body);
  }
}

// promptSharedEditConfirm renders the modal and resolves true=confirm,
// false=cancel. The references[] list is rendered as a bullet list, with
// each item a click-thru to the parent entity's admin page.
function promptSharedEditConfirm(env: AdminSharedEditError): Promise<boolean> {
  return new Promise((resolve) => {
    const title = env.kind === "text" ? "This Text is shared" : "This TextSet is shared";
    const { dialog, content, close } = openModal({ title });

    let resolved = false;
    const finish = (v: boolean): void => {
      if (resolved) return;
      resolved = true;
      resolve(v);
    };
    dialog.addEventListener("close", () => finish(false));

    const intro = document.createElement("p");
    intro.setAttribute("style", "margin: 0 0 8px 0; font-size: 14px; color: #374151; line-height: 1.45;");
    intro.textContent = env.kind === "text"
      ? `Saving will update the Latvian translation everywhere this Text is used. The following ${env.references.length} other reference(s) will be affected:`
      : `Saving will replace the members of this TextSet everywhere it is used. The following ${env.references.length} other reference(s) will be affected:`;
    content.appendChild(intro);

    const list = document.createElement("ul");
    list.setAttribute("style", "margin: 0 0 12px 0; padding-left: 20px; font-size: 13px; color: #111827; max-height: 300px; overflow-y: auto;");
    for (const ref of env.references) {
      const li = document.createElement("li");
      li.setAttribute("style", "margin-bottom: 4px;");
      const parts: string[] = [];
      parts.push(ref.kind);
      parts.push(ref.parent_short_code);
      if (ref.game_short_code && ref.game_short_code !== ref.parent_short_code) {
        parts.push(`(game=${ref.game_short_code})`);
      }
      if (ref.slot) parts.push(`slot=${ref.slot}`);
      li.textContent = parts.join(" · ");
      list.appendChild(li);
    }
    content.appendChild(list);

    const detailLink = document.createElement("a");
    const detailPath = env.kind === "text"
      ? `/admin/texts/${encodeURIComponent(env.id)}`
      : `/admin/textsets/${encodeURIComponent(env.id)}`;
    detailLink.href = detailPath;
    detailLink.textContent = "View full Used-by panel ↗";
    detailLink.setAttribute("style", "display: inline-block; margin-bottom: 12px; font-size: 13px; color: #2563eb; text-decoration: none;");
    detailLink.addEventListener("click", (e) => {
      e.preventDefault();
      history.pushState({}, "", detailPath);
      window.dispatchEvent(new PopStateEvent("popstate"));
      // Close the modal — navigation drops the surrounding screen anyway.
      close();
      finish(false);
    });
    content.appendChild(detailLink);

    const btnRow = document.createElement("div");
    btnRow.setAttribute("style", "display: flex; gap: 8px; justify-content: flex-end;");
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.textContent = "Cancel";
    cancelBtn.setAttribute("style", "padding: 8px 14px; background: #e5e7eb; color: #111827; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
    cancelBtn.addEventListener("click", () => {
      finish(false);
      close();
    });
    const confirmBtn = document.createElement("button");
    confirmBtn.type = "button";
    confirmBtn.textContent = "Update everywhere";
    confirmBtn.setAttribute("style", "padding: 8px 14px; background: #dc2626; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
    confirmBtn.addEventListener("click", () => {
      finish(true);
      close();
    });
    btnRow.append(cancelBtn, confirmBtn);
    content.appendChild(btnRow);
  });
}
